
import { Vue, Component, Prop } from 'vue-property-decorator';
import { AxiosResponse } from 'axios';
import moment from 'moment';
import NextStep from '@/models/NextStep';
import { getFirstAvailableDeliveryDate } from '@/helpers/DeliveryDateHelper';
import TechnologistOrderPrefillAPI from '@/api/TechnologistOrderPrefillAPI';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import TechnologistOrderFormInputs from '@/components/views/NewProjectView/Order/TechnologistOrderFormInputs.vue';
import TechnologistOrderFormOrders from '@/components/views/NewProjectView/Order/TechnologistOrderFormOrders.vue';
import TechnologistOrderFormDates from '@/components/views/NewProjectView/Order/TechnologistOrderFormDates.vue';
import { TechnologistFormOrder } from '@/interfaces/components/projectNew/TechnologistFormOrder';
import { technologistOrderFormConfiguration, prepareTechnologistOrderFormData, refreshTechnologistOrderFormOptions } from '@/helpers/OrderHelper';
import { TechnologistFormField } from '@/interfaces/components/projectNew/TechnologistFormField';
import Offer from '@/models/Offer';
import TechnologistOrder from '@/models/TechnologistOrder';
import { uploadAttachmentWithRelationshipToLead } from '@/helpers/attachments/AttachmentsHelper';
import InternalOrder from '@/models/InternalOrder';
import { UserRepository } from '@/repositories/UserRepository';
import ProductSystem from '@/models/ProductSystem';
import { ProductSystemRepository } from '@/repositories/ProductSystemRepository';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import { TechnologistForm } from '@/interfaces/components/projectNew/TechnologistForm';
import TechnologistOrderAttachmentAPI from '@/api/TechnologistOrderAttachmentAPI';
import ProjectInternalOrderAttachmentAPI from '@/api/ProjectInternalOrderAttachmentAPI';
import { hasCommentOrAttachment } from '@/helpers/OrderValidationHelper';

@Component({
    name: 'TechnologistOrderForm',
    components: {
        TechnologistOrderFormInputs,
        TechnologistOrderFormOrders,
        TechnologistOrderFormDates,
    },
})
export default class TechnologistOrderForm extends Vue {
    @Prop({ required: true }) private project!: any;
    @Prop({ required: true }) private selectedOffer!: any;

    private visible: boolean = false;
    // Create formData based on form fields
    private formData: Partial<Record<keyof TechnologistForm, any>> = {};
    private orders: TechnologistFormOrder[] = [];
    private errors: { [key: string]: string } = {};
    private orderErrors: { [key: string]: { system: boolean; quantity: boolean } } = {};
    private loadingOverlay = new LoadingOverlayHelper(this, {});
    private pendingColorTarget: 'general' | number | null = null;

    private get orderFormConfiguration() {
        return technologistOrderFormConfiguration;
    }

    private get clientsPaymentTypeId() {
        const paymentTypeId = this.project?.client?.paymentType?.id;

        if (!paymentTypeId) {
            return null;
        }

        return paymentTypeId;
    }

    private get systems() {
        return ProductSystemRepository.getAllShallow();
    }

    private async openPopup() {
        this.resetPopupState();
        await refreshTechnologistOrderFormOptions();

        this.visible = true;

        this.setInitialFormValues();
        await this.setInitialDefaults();
    }

    private get workFreeDays(): any[] {
        return this.$store.getters['tableData/workFreeDays'] ?? [];
    }

    private async loadWorkFreeDays() {
        if (this.workFreeDays.length) {
            return;
        }
        try {
            await NextStep.getWorkFreeDays();
        } catch (e) {
            // Without holidays the default is still today + 3 weeks.
        }
    }

    private closePopup() {
        this.visible = false;
    }

    private resetPopupState() {
        this.visible = false;
        // Reset form data
        this.formData = {};
        this.orders = [];
        this.errors = {};
        this.orderErrors = {};
        this.pendingColorTarget = null;
    }

    private openColorPicker(payload: { target: 'general' | number }) {
        this.pendingColorTarget = payload.target;
        EventBus.$emit(EventBusEvents.showColorModule, {
            colorSystemPID: 'material',
            fieldValue: '',
            activeProductFormId: null,
            productName: '',
        });
    }

    private onColorSelected(payload: { color: any; selectedPowder: any }) {
        if (this.pendingColorTarget === null) {
            return;
        }
        const colorData = {
            colorName: payload.color?.name ?? null,
            colorHex: payload.color?.hex ?? null,
            powderId: payload.selectedPowder?.id ? String(payload.selectedPowder.id) : null,
            powderName: payload.selectedPowder?.name ?? null,
            ingeniousColor: payload.color?.ingeniousColor ?? null,
        };
        if (this.pendingColorTarget === 'general') {
            this.$set(this.formData, 'colorData', colorData);
        } else {
            this.$set(this.orders[this.pendingColorTarget], 'colorData', colorData);
        }
        this.pendingColorTarget = null;
    }

    private async setInitialDefaults() {
        (this.formData as any).projectConnection = this.project?.connection ?? '';

        // Start with one empty order so the user does not have to click "Add order" first.
        this.orders = [{ system: '', quantity: 0, attachmentRows: [{ file: null, attachmentType: null }], colorData: null }];
        (this.formData as any).numOfOrders = '1';

        // Dates: commercial order = today, delivery = first date the delivery picker allows
        // (today + 3 weeks, skipping holidays).
        await this.loadWorkFreeDays();
        (this.formData as any).orderAcceptedDate = moment();
        (this.formData as any).deliveryDate = getFirstAvailableDeliveryDate(this.workFreeDays);

        // Document type and technologist: the same pre-selection the preorder reviewer gets.
        try {
            const prefill = await TechnologistOrderPrefillAPI.get(String(this.project?.id));
            if (prefill.documentType) {
                (this.formData as any).documentType = prefill.documentType;
            }
            if (prefill.technologistName) {
                (this.formData as any).technologistName = prefill.technologistName;
            }
        } catch (e) {
            // Prefill is a convenience only; the user can still pick both manually.
        }
    }

    private setInitialFormValues() {
        this.formData = technologistOrderFormConfiguration.formFields.reduce((acc: Record<string, any>, field) => {
            if (field.type === 'number') {
                acc[field.name as keyof TechnologistForm] = 0;
            } else if (field.type === 'textarea' || field.type === 'text') {
                acc[field.name as keyof TechnologistForm] = '';
            } else if (field.type === 'dropdown') {
                acc[field.name as keyof TechnologistForm] = null;
            } else if (field.type === 'attachment') {
                acc[field.name as keyof TechnologistForm] = [];
            } else if (field.type === 'date') {
                acc[field.name as keyof TechnologistForm] = null;
            }
            return acc;
        }, {} as Partial<TechnologistForm>);
    }

    private validateField(field: TechnologistFormField) {
        if (!field.required) {
            delete this.errors[field.name];
            return;
        }

        const value = this.formData[field.name as keyof TechnologistForm];
        let isValid = true;

        if (field.type === 'number' && value === 0) {
            isValid = false;
        } else if ((field.type === 'text' || field.type === 'textarea') && typeof value === 'string' && !value.trim()) {
            isValid = false;
        } else if ((field.type === 'dropdown' || field.type === 'date') && (value === null || value === undefined)) {
            isValid = false;
        } else if (field.type === 'attachment' && Array.isArray(value) && value.length === 0) {
            isValid = false;
        }

        if (!isValid) {
            this.$set(this.errors, field.name, `${this.$t(field?.errorFieldName ?? field.label)}`);
        } else {
            this.$delete(this.errors, field.name);
        }
    }

    private hasAttachmentWithoutType(): boolean {
        const missingType = (row: any) => row.file && !row.existingAttachmentId && !row.attachmentType;
        const orderLevelRows = (this.formData as any).attachmentRows ?? [];
        return (
            orderLevelRows.some(missingType) ||
            this.orders.some((order) => (order.attachmentRows ?? []).some(missingType))
        );
    }

    private validateForm(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.orderFormConfiguration.formFields.forEach((field) => {
                if (field.required) this.validateField(field);
            });

            if (!hasCommentOrAttachment(this.formData)) {
                this.$set(this.errors, 'commentOrAttachment', this.$t('Please add at least a comment or an attachment.') as string);
            } else {
                this.$delete(this.errors, 'commentOrAttachment');
            }

            if (this.hasAttachmentWithoutType()) {
                this.$set(this.errors, 'attachmentType', this.$t('Attachment type') as string);
            } else {
                this.$delete(this.errors, 'attachmentType');
            }

            this.orders.forEach((order, i) => {
                if (!order.system || order.quantity === 0) {
                    this.$set(this.orderErrors, i.toString(), {
                        system: !order.system,
                        quantity: order.quantity === 0,
                    });
                } else {
                    this.$delete(this.orderErrors, i.toString());
                }
            });

            if (Object.keys(this.orderErrors).length > 0) {
                this.$notification.error({
                    message: this.$t('Error') as string,
                    description: this.$t('Please fill all orders.') as string,
                });
                reject({ ...this.orderErrors });
            }

            if (Object.keys(this.errors).length === 0) {
                resolve();
            } else {
                const descriptionMessage = this.$t('Following fields are missing:') as string;
                const errorFields = Object.values(this.errors).join('\n-');

                this.$notification.error({
                    message: this.$t('Error') as string,
                    description: `${descriptionMessage}\n-${errorFields}`,
                });

                reject({ ...this.errors });
            }
        });
    }

    private get currentUser() {
        return UserRepository.getCurrentUser();
    }

    private async submitForm() {
        let offerId;

        try {
            if (this.orders.length === 0) {
                this.$notification.error({
                    message: this.$t('Error') as string,
                    description: this.$t('Please add at least one order.') as string,
                });
                return;
            }

            await this.validateForm();
        } catch (err) {
            console.error('Validation failed:', err);
            return;
        }

        try {
            this.loadingOverlay.start();

            // First step - create offer if there is no selected offer
            if (!this.selectedOffer) {
                const projectId = this.project.id;
                offerId = (await Offer.createNew(String(projectId), this.clientsPaymentTypeId))?.data?.id as string;
                await Offer.getById(offerId);
            }

            await this.$nextTick();

            if (!offerId) {
                offerId = this.selectedOffer.id;
            }

            const formattedFormData = prepareTechnologistOrderFormData(this.formData);
            if (this.formData.colorData) {
                (formattedFormData as any).technologistOrderColorData = this.formData.colorData;
            }

            // Next step, send form data on technologist orders relation on offer
            const technologistOrderFormRequest = await TechnologistOrder.createNew(
                formattedFormData,
                offerId,
                this.currentUser?.id ?? '1',
            );

            // Next step, upload attachments
            await this.uploadAttachments(String(technologistOrderFormRequest?.data?.id));

            // Next step, create internal orders based on form orders
            await this.createOrders(offerId);

            // Last step, create custom technologist order
            await Offer.orderByTechnologist(offerId);
        } catch (err) {
            console.error('Error creating order', err);
        } finally {
            // Update project at the end
            EventBus.$emit(EventBusEvents.updateProject, { shouldUpdateOffer: true });
            this.loadingOverlay.stop();
            this.closePopup();
        }
    }
    
    private async uploadAttachments(technologistOrderId: string) {
        const attachmentRows = (this.formData as any).attachmentRows ?? [];

        const promises = [];
        for (const row of attachmentRows) {
            const file = row.file?.originFileObj;
            if (!file) {
                continue;
            }

            promises.push(TechnologistOrderAttachmentAPI.upload(technologistOrderId, file, row.attachmentType ?? null));
        }

        try {
            await Promise.all(promises);
        } catch (error) {
            console.error(error);
        }
    }

    private async createOrders(newOfferId: string) {
        const promises = [];
        for (const order of this.orders as any[]) {
            promises.push(
                InternalOrder.createNew({ order, projectId: this.project.id, offerId: newOfferId }).then(
                    async (response: AxiosResponse) => {
                        const internalOrderId = (response as any)?.data?.id;
                        if (!internalOrderId) {
                            return;
                        }
                        if (order.colorData) {
                            await InternalOrder.updateColor(String(internalOrderId), order.colorData);
                        }
                        const attachmentRows = order.attachmentRows ?? [];
                        const attachmentPromises = [];
                        for (const row of attachmentRows) {
                            const file = row.file?.originFileObj;
                            if (!file) {
                                continue;
                            }
                            attachmentPromises.push(
                                ProjectInternalOrderAttachmentAPI.upload(String(internalOrderId), file, row.attachmentType ?? null),
                            );
                        }
                        await Promise.all(attachmentPromises);
                    },
                ),
            );
        }
        try {
            await Promise.all(promises);
        } catch (error) {
            console.error(error);
        }
    }

    private addOrder() {
        const order: TechnologistFormOrder = {
            system: '',
            quantity: 0,
            attachmentRows: [{ file: null, attachmentType: null }],
            colorData: null,
        };
        this.orders.push(order);
        this.formData.numOfOrders = this.orders.length.toString();
    }

    private deleteOrder(index: number) {
        this.orders.splice(index, 1);
        this.formData.numOfOrders = this.orders.length.toString();
        this.orderErrors.hasOwnProperty(index.toString()) && this.$delete(this.orderErrors, index.toString());
    }

    private setInitialOrders() {
        if (!this.formData.numOfOrders) {
            return;
        }

        this.orders = Array.from({ length: parseInt(this.formData.numOfOrders) }, () => ({
            system: '',
            quantity: 0,
            attachmentRows: [{ file: null, attachmentType: null }],
            colorData: null,
        }));
    }

    private onNumberOfOrdersChange() {
        if (!this.formData.numOfOrders) {
            return;
        }
        if (this.orders.length === parseInt(this.formData.numOfOrders)) {
            return;
        }
        if (this.orders.length < parseInt(this.formData.numOfOrders)) {
            const difference = parseInt(this.formData.numOfOrders) - this.orders.length;
            for (let i = 0; i < difference; i++) {
                this.orders.push({
                    system: '',
                    quantity: 0,
                    attachmentRows: [{ file: null, attachmentType: null }],
                    colorData: null,
                });
            }
        }
    }

    private async fetchSystems() {
        try {
            this.loadingOverlay.start();
            await ProductSystem.getAll();
        } catch (error) {
            console.error(error);
        } finally {
            this.loadingOverlay.stop();
        }
    }

    private async created() {
        if (this.systems.length < 2) {
            await this.fetchSystems();
        }
        EventBus.$on(EventBusEvents.showTechnologistOrderForm, this.openPopup);
        EventBus.$on(EventBusEvents.selectMaterialColor, this.onColorSelected);
    }

    private beforeDestroy() {
        EventBus.$off(EventBusEvents.showTechnologistOrderForm, this.openPopup);
        EventBus.$off(EventBusEvents.selectMaterialColor, this.onColorSelected);
    }
}
